/**
 * Copyright Zendesk, Inc.
 *
 * Use of this source code is governed under the Apache License, Version 2.0
 * found at http://www.apache.org/licenses/LICENSE-2.0.
 */
import { useUIDSeed } from 'react-uid';
import { composeEventHandlers } from '@zendeskgarden/container-utilities';
import React, { useState, useRef, useCallback, useMemo } from 'react';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var _excluded$1 = ["idPrefix", "ariaLabel", "environment", "type", "min", "max", "orientation", "keyboardStep", "defaultValueNow", "valueNow", "onChange", "position", "rtl"];

var _rtl, _ltr, _horizontalArrowKeys;

var SplitterOrientation;

(function (SplitterOrientation) {
  SplitterOrientation["HORIZONTAL"] = "horizontal";
  SplitterOrientation["VERTICAL"] = "vertical";
})(SplitterOrientation || (SplitterOrientation = {}));

var SplitterPosition;

(function (SplitterPosition) {
  SplitterPosition["LEADS"] = "leads";
  SplitterPosition["TRAILS"] = "trails";
})(SplitterPosition || (SplitterPosition = {}));

var SplitterType;

(function (SplitterType) {
  SplitterType["FIXED"] = "fixed";
  SplitterType["VARIABLE"] = "variable";
})(SplitterType || (SplitterType = {}));

var KEYBOARD_STEP = 48;

var normalizePointerToSeparator = function normalizePointerToSeparator(bodyPadding, pointerPosition, separatorHeightOrWidth, viewportWidthOrHeight) {
  if (separatorHeightOrWidth === void 0) {
    separatorHeightOrWidth = 0;
  }

  if (viewportWidthOrHeight === void 0) {
    viewportWidthOrHeight = 0;
  }

  if (viewportWidthOrHeight === 0) {
    return pointerPosition - bodyPadding - Math.floor(separatorHeightOrWidth / 2);
  }

  return viewportWidthOrHeight - pointerPosition - bodyPadding - Math.floor(separatorHeightOrWidth / 2);
};

var verticalArrowKeys = {
  rtl: (_rtl = {}, _rtl[SplitterPosition.TRAILS] = {
    INCREASE: 'ArrowLeft',
    DECREASE: 'ArrowRight'
  }, _rtl[SplitterPosition.LEADS] = {
    INCREASE: 'ArrowRight',
    DECREASE: 'ArrowLeft'
  }, _rtl),
  ltr: (_ltr = {}, _ltr[SplitterPosition.LEADS] = {
    INCREASE: 'ArrowLeft',
    DECREASE: 'ArrowRight'
  }, _ltr[SplitterPosition.TRAILS] = {
    INCREASE: 'ArrowRight',
    DECREASE: 'ArrowLeft'
  }, _ltr)
};
var horizontalArrowKeys = (_horizontalArrowKeys = {}, _horizontalArrowKeys[SplitterPosition.LEADS] = {
  INCREASE: 'ArrowUp',
  DECREASE: 'ArrowDown'
}, _horizontalArrowKeys[SplitterPosition.TRAILS] = {
  INCREASE: 'ArrowDown',
  DECREASE: 'ArrowUp'
}, _horizontalArrowKeys);

var xor = function xor(a, b) {
  if (a && b) {
    return false;
  }

  return a || b;
};

function useSplitter(_ref) {
  var idPrefix = _ref.idPrefix,
      ariaLabel = _ref.ariaLabel,
      environment = _ref.environment,
      type = _ref.type,
      min = _ref.min,
      max = _ref.max,
      orientation = _ref.orientation,
      _ref$keyboardStep = _ref.keyboardStep,
      keyboardStep = _ref$keyboardStep === void 0 ? KEYBOARD_STEP : _ref$keyboardStep,
      _ref$defaultValueNow = _ref.defaultValueNow,
      defaultValueNow = _ref$defaultValueNow === void 0 ? min : _ref$defaultValueNow,
      valueNow = _ref.valueNow,
      _ref$onChange = _ref.onChange,
      onChange = _ref$onChange === void 0 ? function () {
    return undefined;
  } : _ref$onChange,
      position = _ref.position,
      rtl = _ref.rtl,
      props = _objectWithoutPropertiesLoose(_ref, _excluded$1);

  var seed = useUIDSeed();

  var _useState = useState(idPrefix || seed("splitter_" + '0.2.3')),
      prefix = _useState[0];

  var PRIMARY_PANE_ID = prefix + "--primary-pane";
  var isControlled = valueNow !== undefined && valueNow !== null;

  var _useState2 = useState(defaultValueNow),
      state = _useState2[0],
      setState = _useState2[1];

  var offsetRef = useRef({
    left: 0,
    right: 0,
    top: 0,
    bottom: 0
  });
  var separatorRef = useRef(null);
  var separatorPosition = isControlled ? valueNow : state;
  var setSeparatorPosition = isControlled ? onChange : setState;
  var setRangedSeparatorPosition = useCallback(function (nextDimension) {
    if (nextDimension >= max) {
      setSeparatorPosition(max);
    } else if (nextDimension <= min) {
      setSeparatorPosition(min);
    } else {
      setSeparatorPosition(nextDimension);
    }
  }, [setSeparatorPosition, min, max]);
  var updateOffsets = useCallback(function () {
    var rect = separatorRef.current.getBoundingClientRect();
    var clientWidth = environment.document.body.clientWidth;
    var clientHeight = environment.document.body.clientHeight;
    offsetRef.current.left = rect.left - separatorPosition + environment.scrollX;
    offsetRef.current.right = clientWidth - rect.right - separatorPosition - environment.scrollX;
    offsetRef.current.top = rect.top - separatorPosition + environment.scrollY;
    offsetRef.current.bottom = clientHeight - rect.bottom - separatorPosition - environment.scrollY;
  }, [offsetRef, separatorRef, separatorPosition, environment]);
  var onSplitterMouseMove = useCallback(function (event) {
    var elem = separatorRef.current;
    var clientWidth = xor(rtl, position === SplitterPosition.LEADS) ? environment.document.body.clientWidth : undefined;
    var clientHeight = position === SplitterPosition.LEADS ? environment.document.body.clientHeight : undefined;

    if (orientation === SplitterOrientation.HORIZONTAL) {
      var offset = position === SplitterPosition.LEADS ? offsetRef.current.bottom : offsetRef.current.top;
      setRangedSeparatorPosition(normalizePointerToSeparator(offset, event.pageY, elem == null ? void 0 : elem.offsetHeight, clientHeight));
    } else {
      var _offset = xor(rtl, position === SplitterPosition.LEADS) ? offsetRef.current.right : offsetRef.current.left;

      setRangedSeparatorPosition(normalizePointerToSeparator(_offset, event.pageX, elem == null ? void 0 : elem.offsetWidth, clientWidth));
    }
  }, [orientation, setRangedSeparatorPosition, position, environment, rtl]);
  var onMouseMove = useMemo(function () {
    return composeEventHandlers(props.onMouseMove, onSplitterMouseMove);
  }, [props.onMouseMove, onSplitterMouseMove]);
  var onSplitterTouchMove = useCallback(function (event) {
    var _event$targetTouches$ = event.targetTouches[0],
        pageY = _event$targetTouches$.pageY,
        pageX = _event$targetTouches$.pageX;
    var elem = separatorRef.current;
    var clientWidth = xor(rtl, position === SplitterPosition.LEADS) ? environment.document.body.clientWidth : undefined;
    var clientHeight = position === SplitterPosition.LEADS ? environment.document.body.clientHeight : undefined;

    if (orientation === SplitterOrientation.HORIZONTAL) {
      var offset = position === SplitterPosition.LEADS ? offsetRef.current.bottom : offsetRef.current.top;
      setRangedSeparatorPosition(normalizePointerToSeparator(offset, pageY, elem == null ? void 0 : elem.offsetHeight, clientHeight));
    } else {
      var _offset2 = xor(rtl, position === SplitterPosition.LEADS) ? offsetRef.current.right : offsetRef.current.left;

      setRangedSeparatorPosition(normalizePointerToSeparator(_offset2, pageX, elem == null ? void 0 : elem.offsetWidth, clientWidth));
    }
  }, [orientation, setRangedSeparatorPosition, position, environment, rtl]);
  var onTouchMove = useMemo(function () {
    return composeEventHandlers(props.onTouchMove, onSplitterTouchMove);
  }, [props.onTouchMove, onSplitterTouchMove]);
  var onMouseLeaveOrUp = useMemo(function () {
    return composeEventHandlers(props.onMouseUp, props.onMouseLeave, function () {
      environment.document.removeEventListener('mouseup', onMouseLeaveOrUp);
      environment.document.body.removeEventListener('mouseleave', onMouseLeaveOrUp);
      environment.document.removeEventListener('mousemove', onMouseMove);
    });
  }, [environment, props.onMouseUp, props.onMouseLeave, onMouseMove]);
  var onTouchEnd = useMemo(function () {
    return composeEventHandlers(props.onTouchEnd, function () {
      environment.document.removeEventListener('touchend', onTouchEnd);
      environment.document.removeEventListener('touchmove', onTouchMove);
    });
  }, [environment, props.onTouchEnd, onTouchMove]);
  var onMouseDown = composeEventHandlers(props.onMouseDown, function () {
    if (type === SplitterType.FIXED) {
      if (separatorPosition > min) {
        setSeparatorPosition(min);
      }

      if (separatorPosition < max) {
        setSeparatorPosition(max);
      }
    } else {
      updateOffsets();
      environment.document.addEventListener('mouseup', onMouseLeaveOrUp);
      environment.document.body.addEventListener('mouseleave', onMouseLeaveOrUp);
      environment.document.addEventListener('mousemove', onMouseMove);
    }
  });
  var onTouchStart = composeEventHandlers(props.onTouchStart, function () {
    if (type === SplitterType.FIXED) {
      if (separatorPosition > min) {
        setSeparatorPosition(min);
      }

      if (separatorPosition < max) {
        setSeparatorPosition(max);
      }
    } else {
      updateOffsets();
      environment.document.addEventListener('touchend', onTouchEnd);
      environment.document.addEventListener('touchmove', onTouchMove);
    }
  });
  var toggleMinMax = useCallback(function () {
    if (separatorPosition === min) {
      setSeparatorPosition(max);
    } else {
      setSeparatorPosition(min);
    }
  }, [separatorPosition, setSeparatorPosition, min, max]);
  var onKeyDown = composeEventHandlers(props.onKeyDown, function (event) {
    var rtlKey = rtl ? 'rtl' : 'ltr';

    if (orientation === SplitterOrientation.VERTICAL) {
      switch (event.key) {
        case verticalArrowKeys[rtlKey][position].DECREASE:
          type === SplitterType.VARIABLE && setRangedSeparatorPosition(separatorPosition - keyboardStep);
          break;

        case verticalArrowKeys[rtlKey][position].INCREASE:
          type === SplitterType.VARIABLE && setRangedSeparatorPosition(separatorPosition + keyboardStep);
          break;

        case 'Enter':
          toggleMinMax();
          break;
      }
    } else {
      switch (event.key) {
        case horizontalArrowKeys[position].DECREASE:
          type === SplitterType.VARIABLE && setRangedSeparatorPosition(separatorPosition - keyboardStep);
          break;

        case horizontalArrowKeys[position].INCREASE:
          type === SplitterType.VARIABLE && setRangedSeparatorPosition(separatorPosition + keyboardStep);
          break;

        case 'Enter':
          {
            toggleMinMax();
            break;
          }
      }
    }
  });
  var getSeparatorProps = useCallback(function (_temp) {
    var _ref2 = _temp === void 0 ? {} : _temp,
        other = _extends({}, _ref2);

    return _extends({
      'data-garden-container-id': 'containers.splitter.separator',
      'data-garden-container-version': '0.2.3',
      role: 'separator',
      ref: separatorRef,
      onMouseDown: onMouseDown,
      onKeyDown: onKeyDown,
      onTouchStart: onTouchStart,
      'aria-label': ariaLabel,
      'aria-controls': PRIMARY_PANE_ID,
      'aria-valuenow': separatorPosition,
      'aria-valuemin': min,
      'aria-valuemax': max,
      'aria-orientation': orientation,
      tabIndex: 0
    }, other);
  }, [separatorRef, onMouseDown, onKeyDown, onTouchStart, ariaLabel, PRIMARY_PANE_ID, separatorPosition, min, max, orientation]);
  var getPrimaryPaneProps = useCallback(function (_temp2) {
    var _ref3 = _temp2 === void 0 ? {} : _temp2,
        other = _extends({}, _ref3);

    return _extends({
      'data-garden-container-id': 'containers.splitter.primaryPane',
      'data-garden-container-version': '0.2.3',
      id: PRIMARY_PANE_ID
    }, other);
  }, [PRIMARY_PANE_ID]);
  return useMemo(function () {
    return {
      getSeparatorProps: getSeparatorProps,
      getPrimaryPaneProps: getPrimaryPaneProps,
      valueNow: separatorPosition
    };
  }, [separatorPosition, getSeparatorProps, getPrimaryPaneProps]);
}

var _excluded = ["children", "render"];

var SplitterContainer = function SplitterContainer(props) {
  var children = props.children,
      _props$render = props.render,
      render = _props$render === void 0 ? children : _props$render,
      options = _objectWithoutPropertiesLoose(props, _excluded);

  return React.createElement(React.Fragment, null, render(useSplitter(options)));
};

SplitterContainer.defaultProps = {
  keyboardStep: KEYBOARD_STEP
};
export { SplitterContainer, SplitterOrientation, SplitterPosition, SplitterType, useSplitter };